/**
 * BrandKit — corporate identity manager for the Content Planner.
 *
 *   • Reads /api/brand/kit (auto-seeded with HidroVerse CI v2.0 on first load)
 *   • Lets the operator edit brand colours, fonts, tagline, voice, forbidden uses
 *   • Logo upload slots (primary / monochrome / icon) reuse the visuals
 *     upload endpoint so files land in GridFS
 *   • PUT saves the kit; from then on every visuals/generate call
 *     automatically prepends the BRAND CONTEXT block to its prompt
 *     so Nano Banana renders on-brand.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Trash2, Plus, RefreshCcw, UploadCloud, Save } from "lucide-react";
import ContentPlannerLayout from "../components/layout/ContentPlannerLayout";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_ADMIN = "hv_admin_token";

const LOGO_SLOTS = [
  { key: "primary",          label: "Primary logo",            help: "Full colour wordmark — main use" },
  { key: "monochrome_teal",  label: "Monochrome · Teal",       help: "Solid teal version" },
  { key: "monochrome_dark",  label: "Monochrome · Dark",       help: "Solid dark grey/black version" },
  { key: "monochrome_white", label: "Monochrome · White",      help: "Reverse — for dark backgrounds" },
  { key: "icon_only",        label: "Icon (mark only)",        help: "Stand-alone droplet — avatar, favicon" },
];

const COLOUR_ROLES = [
  { value: "primary", label: "Primary" },
  { value: "accent",  label: "Accent" },
  { value: "neutral", label: "Neutral" },
];

export default function BrandKit() {
  const token = (typeof window !== "undefined") ? localStorage.getItem(STORAGE_ADMIN) : "";
  const [kit, setKit]       = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const auth = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/brand/kit`, { headers: auth() });
      const d = await r.json();
      if (r.ok && d.kit) setKit(d.kit);
      else setStatus({ kind: "err", msg: d.detail || "Could not load brand kit." });
    } catch (e) { setStatus({ kind: "err", msg: e.message }); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setStatus(null);
    try {
      const r = await fetch(`${API}/api/brand/kit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify(kit),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not save brand kit.");
      setKit(d.kit);
      setStatus({ kind: "ok", msg: "Saved. Future visuals will use this brand context." });
    } catch (e) { setStatus({ kind: "err", msg: e.message }); }
    finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset every field to the HidroVerse CI defaults?")) return;
    try {
      const r = await fetch(`${API}/api/brand/reset`, { method: "POST", headers: auth() });
      const d = await r.json();
      if (r.ok && d.kit) {
        setKit(d.kit);
        setStatus({ kind: "ok", msg: "Reset to HidroVerse CI v2.0 defaults." });
      } else {
        setStatus({ kind: "err", msg: d.detail || "Reset failed." });
      }
    } catch (e) { setStatus({ kind: "err", msg: e.message }); }
  };

  const uploadLogo = async (slotKey, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("format", "ig-square");
    try {
      const r = await fetch(`${API}/api/content-calendar/visuals/upload`, {
        method: "POST", headers: auth(), body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Upload failed");
      setKit((prev) => ({
        ...prev,
        logos: { ...(prev.logos || {}), [slotKey]: d.url },
      }));
      setStatus({ kind: "ok", msg: `${slotKey} logo uploaded. Click Save to persist.` });
    } catch (e) { setStatus({ kind: "err", msg: e.message }); }
  };

  const patch = (k, v) => setKit((prev) => ({ ...prev, [k]: v }));
  const patchContact = (k, v) => setKit((prev) => ({
    ...prev, contact: { ...(prev.contact || {}), [k]: v },
  }));
  const patchColour = (idx, k, v) => setKit((prev) => ({
    ...prev,
    colours: prev.colours.map((c, i) => i === idx ? { ...c, [k]: v } : c),
  }));
  const addColour    = () => setKit((prev) => ({
    ...prev, colours: [...(prev.colours || []), { name: "New", hex: "#000000", role: "neutral" }],
  }));
  const removeColour = (idx) => setKit((prev) => ({
    ...prev, colours: prev.colours.filter((_, i) => i !== idx),
  }));
  const patchForbidden = (idx, v) => setKit((prev) => ({
    ...prev, forbidden: prev.forbidden.map((s, i) => i === idx ? v : s),
  }));
  const addForbidden    = () => setKit((prev) => ({
    ...prev, forbidden: [...(prev.forbidden || []), ""],
  }));
  const removeForbidden = (idx) => setKit((prev) => ({
    ...prev, forbidden: prev.forbidden.filter((_, i) => i !== idx),
  }));

  if (!kit) {
    return (
      <ContentPlannerLayout>
        <Helmet><title>Brand kit — HidroVerse</title><meta name="robots" content="noindex,nofollow" /></Helmet>
        <div data-testid="brand-kit-loading" className="font-mono uppercase"
             style={{ fontSize: "0.65rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)" }}>
          Loading…
        </div>
      </ContentPlannerLayout>
    );
  }

  return (
    <ContentPlannerLayout
      topBarActions={
        <>
          <button type="button" onClick={resetDefaults}
                  data-testid="brand-reset"
                  className="hv-pill"
                  style={{ padding: "8px 14px", background: "#FFFFFF", color: "#0C2A40",
                           border: "1px solid rgba(12,42,64,0.12)",
                           display: "inline-flex", alignItems: "center", gap: 6 }}>
            <RefreshCcw size={13} /> Reset defaults
          </button>
          <button type="button" onClick={save} disabled={saving}
                  data-testid="brand-save"
                  className="hv-pill"
                  style={{ padding: "8px 16px", background: "#1FB6C1", color: "#0C2A40",
                           border: "1px solid #1FB6C1", fontWeight: 700,
                           display: "inline-flex", alignItems: "center", gap: 6,
                           boxShadow: "0 6px 18px -6px rgba(31,182,193,0.45)" }}>
            <Save size={13} /> {saving ? "Saving…" : "Save brand kit"}
          </button>
        </>
      }
    >
      <Helmet><title>Brand kit — HidroVerse</title><meta name="robots" content="noindex,nofollow" /></Helmet>

      <div data-testid="brand-kit-page" style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="mb-5 p-4"
             style={{ background: "#F3FBFB", border: "1px solid rgba(31,182,193,0.30)",
                      borderRadius: 12 }}>
          <p className="font-light leading-relaxed"
             style={{ fontSize: "0.86rem", color: "#0C2A40" }}>
            This kit is the single source of truth for everything Gemini
            Nano Banana renders. The BRAND CONTEXT block is auto-prepended
            to every visual prompt — colours, tone, forbidden uses, and
            imagery style all flow through. Upload your real logos so the
            backend can overlay them pixel-perfectly when needed.
          </p>
        </div>

        {status && (
          <div data-testid="brand-status"
               className="mb-4 px-4 py-2"
               style={{
                 background: status.kind === "ok" ? "rgba(31,182,193,0.12)" : "rgba(184,92,56,0.12)",
                 border: `1px solid ${status.kind === "ok" ? "rgba(31,182,193,0.40)" : "rgba(184,92,56,0.40)"}`,
                 color: status.kind === "ok" ? "#0E7A82" : "#A14523",
                 borderRadius: 10, fontSize: "0.85rem",
               }}>
            {status.msg}
          </div>
        )}

        {/* IDENTITY */}
        <Section title="Identity">
          <Row>
            <Field label="Brand name">
              <input className="hv-input" value={kit.brand_name || ""}
                     onChange={(e) => patch("brand_name", e.target.value)}
                     data-testid="brand-name" />
            </Field>
            <Field label="Tagline">
              <input className="hv-input" value={kit.tagline || ""}
                     onChange={(e) => patch("tagline", e.target.value)}
                     data-testid="brand-tagline" />
            </Field>
            <Field label="Secondary tagline">
              <input className="hv-input" value={kit.secondary_tagline || ""}
                     onChange={(e) => patch("secondary_tagline", e.target.value)}
                     data-testid="brand-secondary-tagline" />
            </Field>
          </Row>
        </Section>

        {/* CONTACT */}
        <Section title="Contact" hint="Surfaced in some auto-generated assets.">
          <Row>
            <Field label="Website">
              <input className="hv-input" value={kit.contact?.website || ""}
                     onChange={(e) => patchContact("website", e.target.value)}
                     data-testid="brand-website" />
            </Field>
            <Field label="Email">
              <input className="hv-input" value={kit.contact?.email || ""}
                     onChange={(e) => patchContact("email", e.target.value)}
                     data-testid="brand-email" />
            </Field>
            <Field label="Phone">
              <input className="hv-input" value={kit.contact?.phone || ""}
                     onChange={(e) => patchContact("phone", e.target.value)}
                     data-testid="brand-phone" />
            </Field>
            <Field label="Address" span={3}>
              <input className="hv-input" value={kit.contact?.address || ""}
                     onChange={(e) => patchContact("address", e.target.value)}
                     data-testid="brand-address" />
            </Field>
          </Row>
        </Section>

        {/* LOGOS */}
        <Section title="Logos" hint="Upload PNG / SVG. Each slot maps to a different usage context.">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {LOGO_SLOTS.map((slot) => (
              <LogoSlot
                key={slot.key}
                slot={slot}
                url={kit.logos?.[slot.key]}
                onUpload={(file) => uploadLogo(slot.key, file)}
                onClear={() => setKit((prev) => ({
                  ...prev, logos: { ...(prev.logos || {}), [slot.key]: null },
                }))}
              />
            ))}
          </div>
        </Section>

        {/* COLOURS */}
        <Section title="Brand colours"
                 hint="Gemini receives these exact HEX values — no substitutes."
                 action={
                   <button type="button" onClick={addColour}
                           data-testid="brand-add-colour"
                           className="font-mono uppercase inline-flex items-center gap-1"
                           style={{ fontSize: "0.62rem", letterSpacing: "0.18em",
                                    color: "#0E7A82", fontWeight: 700,
                                    border: "1px dashed rgba(31,182,193,0.55)",
                                    padding: "5px 10px", borderRadius: 999 }}>
                     <Plus size={12} /> Add colour
                   </button>
                 }>
          <div className="space-y-2">
            {kit.colours.map((c, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center"
                   data-testid={`brand-colour-row-${i}`}>
                <div className="col-span-1" style={{
                  width: 44, height: 44, borderRadius: 8,
                  background: c.hex, border: "1px solid rgba(12,42,64,0.12)",
                  boxShadow: "inset 0 0 0 2px #FFFFFF",
                }} />
                <input className="hv-input col-span-3" value={c.name}
                       onChange={(e) => patchColour(i, "name", e.target.value)}
                       data-testid={`brand-colour-name-${i}`} />
                <input className="hv-input col-span-3" value={c.hex}
                       onChange={(e) => patchColour(i, "hex", e.target.value)}
                       data-testid={`brand-colour-hex-${i}`}
                       style={{ fontFamily: "ui-monospace, monospace" }} />
                <select className="hv-input col-span-3" value={c.role || "neutral"}
                        onChange={(e) => patchColour(i, "role", e.target.value)}
                        data-testid={`brand-colour-role-${i}`}>
                  {COLOUR_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <button type="button" onClick={() => removeColour(i)}
                        title="Remove colour"
                        data-testid={`brand-colour-remove-${i}`}
                        className="col-span-2 inline-flex items-center justify-center gap-1"
                        style={{ background: "#FFFFFF", color: "#B85C38",
                                 border: "1px solid rgba(184,92,56,0.35)",
                                 padding: "8px 10px", borderRadius: 8,
                                 fontSize: "0.7rem", fontWeight: 600 }}>
                  <Trash2 size={12} /> Remove
                </button>
              </div>
            ))}
          </div>
        </Section>

        {/* FONTS */}
        <Section title="Typography" hint="Used in the BRAND CONTEXT block — Gemini will favour these fonts where it can render text.">
          <Row>
            <Field label="Primary family">
              <input className="hv-input"
                     value={kit.fonts?.primary_family || ""}
                     onChange={(e) => patch("fonts", { ...(kit.fonts || {}), primary_family: e.target.value })}
                     data-testid="brand-font-family" />
            </Field>
            <Field label="Substitution rule" span={2}>
              <input className="hv-input"
                     value={kit.fonts?.rule || ""}
                     onChange={(e) => patch("fonts", { ...(kit.fonts || {}), rule: e.target.value })}
                     data-testid="brand-font-rule" />
            </Field>
          </Row>
        </Section>

        {/* VOICE */}
        <Section title="Voice &amp; personality">
          <Row>
            <Field label="Tone" span={3}>
              <textarea className="hv-input"
                        rows={2}
                        value={kit.voice?.tone || ""}
                        onChange={(e) => patch("voice", { ...(kit.voice || {}), tone: e.target.value })}
                        data-testid="brand-voice-tone" />
            </Field>
            <Field label="Personality keywords (comma-separated)" span={3}>
              <input className="hv-input"
                     value={(kit.voice?.personality || []).join(", ")}
                     onChange={(e) => patch("voice", {
                       ...(kit.voice || {}),
                       personality: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                     })}
                     data-testid="brand-voice-personality" />
            </Field>
          </Row>
        </Section>

        {/* FORBIDDEN */}
        <Section title="Forbidden uses"
                 hint="Hard rules. Gemini is told to AVOID these in every render."
                 action={
                   <button type="button" onClick={addForbidden}
                           data-testid="brand-add-forbidden"
                           className="font-mono uppercase inline-flex items-center gap-1"
                           style={{ fontSize: "0.62rem", letterSpacing: "0.18em",
                                    color: "#A14523", fontWeight: 700,
                                    border: "1px dashed rgba(184,92,56,0.45)",
                                    padding: "5px 10px", borderRadius: 999 }}>
                     <Plus size={12} /> Add rule
                   </button>
                 }>
          <div className="space-y-2">
            {kit.forbidden.map((f, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center"
                   data-testid={`brand-forbidden-row-${i}`}>
                <input className="hv-input col-span-11" value={f}
                       onChange={(e) => patchForbidden(i, e.target.value)}
                       data-testid={`brand-forbidden-${i}`} />
                <button type="button" onClick={() => removeForbidden(i)}
                        title="Remove rule"
                        data-testid={`brand-forbidden-remove-${i}`}
                        className="col-span-1 inline-flex items-center justify-center"
                        style={{ background: "#FFFFFF", color: "#B85C38",
                                 border: "1px solid rgba(184,92,56,0.35)",
                                 padding: "8px 10px", borderRadius: 8 }}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </Section>

        {/* IMAGERY STYLE */}
        <Section title="Imagery style"
                 hint="A single paragraph Gemini reads as a styling directive.">
          <textarea className="hv-input"
                    rows={4}
                    value={kit.imagery_style || ""}
                    onChange={(e) => patch("imagery_style", e.target.value)}
                    data-testid="brand-imagery-style" />
        </Section>
      </div>
    </ContentPlannerLayout>
  );
}

/* ─── tiny presentational primitives ─── */

function Section({ title, hint, action, children }) {
  return (
    <section className="mb-6 p-5"
             style={{ background: "#FFFFFF",
                      border: "1px solid rgba(12,42,64,0.08)",
                      borderRadius: 12 }}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-mono uppercase"
               style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                        color: "#0C2A40", fontWeight: 700 }}>
            {title}
          </div>
          {hint && (
            <div className="mt-1 font-light"
                 style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.6)" }}>
              {hint}
            </div>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>;
}

function Field({ label, span, children }) {
  const cls = span === 2 ? "md:col-span-2" : span === 3 ? "md:col-span-3" : "";
  return (
    <div className={cls}>
      <div className="font-mono uppercase mb-1.5"
           style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                    color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function LogoSlot({ slot, url, onUpload, onClear }) {
  const id = `logo-upload-${slot.key}`;
  return (
    <div className="hv-card-soft"
         style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}
         data-testid={`brand-logo-slot-${slot.key}`}>
      <div className="font-mono uppercase"
           style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                    color: "#0C2A40", fontWeight: 700 }}>
        {slot.label}
      </div>
      <div className="font-light"
           style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}>
        {slot.help}
      </div>
      <div style={{
        height: 110, borderRadius: 8,
        background: "#FAFAF7",
        border: "1px dashed rgba(12,42,64,0.18)",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}>
        {url ? (
          <img src={url} alt={slot.label}
               style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        ) : (
          <span className="font-mono uppercase"
                style={{ fontSize: "0.56rem", letterSpacing: "0.2em",
                         color: "rgba(12,42,64,0.45)" }}>
            no file
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <label htmlFor={id}
               className="font-mono uppercase inline-flex items-center gap-1 cursor-pointer"
               style={{ flex: 1, justifyContent: "center",
                        background: "#0C2A40", color: "#FFFFFF",
                        padding: "6px 10px", borderRadius: 8,
                        fontSize: "0.6rem", letterSpacing: "0.18em",
                        fontWeight: 700 }}>
          <UploadCloud size={12} /> Upload
        </label>
        <input id={id} type="file" hidden
               accept="image/png,image/svg+xml,image/jpeg,image/webp"
               onChange={(e) => onUpload(e.target.files?.[0])}
               data-testid={`brand-logo-input-${slot.key}`} />
        {url && (
          <button type="button" onClick={onClear}
                  title="Clear this slot"
                  data-testid={`brand-logo-clear-${slot.key}`}
                  style={{ background: "#FFFFFF", color: "#B85C38",
                           border: "1px solid rgba(184,92,56,0.35)",
                           padding: "6px 8px", borderRadius: 8 }}>
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
}
